import { API_V1 } from '../config'
import { apiFetch } from './api'

// --- Types matching backend serializers ---

export interface ActorSummary {
  koblink_id: string
  display_name: string
}

export interface NegotiationParty {
  id: number
  actor: ActorSummary | null
  party_label: string
  tag_name: string
  color: string
  role: 'OWNER' | 'CUSTOMER' | 'COUNTERPARTY' | 'TEAM'
  can_send_version: boolean
  can_send_to_signing: boolean
  is_ready_to_sign: boolean
  is_paying: boolean
  is_active: boolean
  is_silent: boolean
  digest_frequency: 'NONE' | 'DAILY' | 'WEEKLY'
  has_left: boolean
  left_at: string | null
  created_at: string
}

export interface NegotiationVersion {
  id: number
  version_number: number
  file_hash: string
  sent_by: ActorSummary | null
  note: string
  has_paragraph_map: boolean
  upload_source: 'addin' | 'web' | 'unknown'
  paragraph_overlap_pct: number | null
  created_at: string
}

export interface Negotiation {
  id: number
  koblink_id: string
  title: string
  owner: ActorSummary | null
  status: 'ACTIVE' | 'PAUSED' | 'COMPLETED' | 'CANCELLED'
  sub_status: string
  current_version: number
  deadline: string | null
  parties: NegotiationParty[]
  ready_count: number
  total_parties: number
  created_at: string
  updated_at: string
}

export interface NegotiationMilestone {
  id: number
  title: string
  description: string
  deadline: string
  milestone_type: string
  status: 'UPCOMING' | 'DUE_SOON' | 'OVERDUE' | 'COMPLETED' | 'WAIVED'
  sort_order: number
  is_blocker: boolean
  completed_at: string | null
  completed_by_display: ActorSummary | null
  created_by_display: ActorSummary | null
  created_at: string
  updated_at: string
}

export type DocumentRole = 'PRIMARY' | 'ANNEX' | 'SIDE_LETTER' | 'SCHEDULE' | 'DISCLOSURE' | 'OTHER'

export interface NegotiationDocumentInfo {
  id: number
  document_koblink_id: string | null
  document_title: string
  role: DocumentRole
  label: string
  sort_order: number
  current_version: number
  access_mode: 'ALL' | 'SPECIFIC'
  is_signable: boolean
  created_at: string
}

export interface NegotiationDetail extends Negotiation {
  versions: NegotiationVersion[]
  document_title: string
  document_koblink_id: string | null
  milestones: NegotiationMilestone[]
  documents: NegotiationDocumentInfo[]
  signing_order: 'PARALLEL' | 'SEQUENTIAL'
}

// --- Comment types ---

export interface CommentAnchor {
  id: number
  word_cc_id: string
  para_id: string
  anchor_text: string
  anchor_paragraph: number | null
  is_primary: boolean
}

export interface CommentAttachment {
  id: number
  filename: string
  file_size: number
  content_type: string
  file_hash: string
  uploaded_by: ActorSummary | null
  created_at: string
}

export interface CommentReaction {
  emoji: string
  count: number
  reacted_by_me: boolean
}

export interface NegotiationComment {
  id: number
  version: number
  author: ActorSummary | null
  content: string
  comment_type: 'USER' | 'SYSTEM'
  word_cc_tag: string
  visibility: 'INTERNAL' | 'ALL' | 'SPECIFIC'
  visible_to: Array<{ id: number; tag_name: string; party_label: string }>
  mentions: ActorSummary[]
  reactions: CommentReaction[]
  parent_comment_id: number | null
  resolved: boolean
  resolved_at: string | null
  is_deleted: boolean
  edited_at: string | null
  created_at: string
  anchors: CommentAnchor[]
  comment_attachments: CommentAttachment[]
  reply_count: number
  needs_classification: boolean
  content_history: Array<{ content: string; edited_at: string }> | null
  replies?: NegotiationComment[]
}

// --- Custom error for 409 Conflict ---

export class VersionConflictError extends Error {
  currentVersion: number
  sentBy?: string
  createdAt?: string
  constructor(currentVersion: number, sentBy?: string, createdAt?: string) {
    super('Version conflict')
    this.name = 'VersionConflictError'
    this.currentVersion = currentVersion
    this.sentBy = sentBy
    this.createdAt = createdAt
  }
}

// --- API functions ---

/** List negotiations for the current actor */
export async function listNegotiations(params?: {
  status?: string
  actorId?: string
  documentTitle?: string
}): Promise<Negotiation[]> {
  const searchParams = new URLSearchParams()
  if (params?.status) searchParams.set('status', params.status)
  if (params?.actorId) searchParams.set('actor_id', params.actorId)
  if (params?.documentTitle) searchParams.set('document_title', params.documentTitle)

  const query = searchParams.toString()
  const url = `${API_V1}/negotiations/${query ? `?${query}` : ''}`
  const response = await apiFetch(url)

  if (!response.ok) {
    throw new Error(`Failed to fetch negotiations: ${response.status}`)
  }
  return response.json()
}

/** Get negotiation detail by ID */
export async function getNegotiation(id: number, actorId?: string): Promise<NegotiationDetail> {
  const params = actorId ? `?actor_id=${encodeURIComponent(actorId)}` : ''
  const response = await apiFetch(`${API_V1}/negotiations/${id}/${params}`)

  if (!response.ok) {
    throw new Error(`Failed to fetch negotiation: ${response.status}`)
  }
  return response.json()
}

/** Create a new negotiation */
export async function createNegotiation(data: {
  document_id: string
  title?: string
  deadline?: string
  owner_party_label?: string
  owner_tag_name?: string
  actor_id?: string
}): Promise<NegotiationDetail> {
  // Pass actor_id both as query param (survives Office cache) and in body
  const url = data.actor_id
    ? `${API_V1}/negotiations/create/?actor_id=${encodeURIComponent(data.actor_id)}`
    : `${API_V1}/negotiations/create/`
  const response = await apiFetch(url, {
    method: 'POST',
    body: JSON.stringify(data),
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => null)
    const message = errorData?.error || `Failed to create negotiation: ${response.status}`
    throw new Error(message)
  }
  return response.json()
}

/** Add a party to a negotiation */
export async function addParty(
  negotiationId: number,
  data: {
    actor_id?: string
    email?: string
    party_label: string
    tag_name: string
    role?: 'CUSTOMER' | 'COUNTERPARTY' | 'TEAM'
    color?: string
  },
): Promise<NegotiationParty> {
  const response = await apiFetch(`${API_V1}/negotiations/${negotiationId}/parties/`, {
    method: 'POST',
    body: JSON.stringify(data),
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => null)
    const message = errorData?.error || `Failed to add party: ${response.status}`
    throw new Error(message)
  }
  return response.json()
}

/** Upload a new version (multipart FormData with optimistic locking) */
export async function uploadVersion(
  negotiationId: number,
  file: Blob,
  expectedVersion: number,
  note?: string,
): Promise<NegotiationVersion> {
  const formData = new FormData()
  formData.append('file', file, 'document.docx')
  formData.append('expected_version', String(expectedVersion))
  if (note) formData.append('note', note)

  const response = await apiFetch(`${API_V1}/negotiations/${negotiationId}/versions/upload/`, {
    method: 'POST',
    body: formData,
  })

  if (response.status === 409) {
    const errorData = await response.json().catch(() => null)
    throw new VersionConflictError(
      errorData?.current_version ?? expectedVersion + 1,
      errorData?.sent_by,
      errorData?.created_at,
    )
  }

  if (!response.ok) {
    const errorData = await response.json().catch(() => null)
    const message = errorData?.error || `Failed to upload version: ${response.status}`
    throw new Error(message)
  }
  return response.json()
}

/** Download a specific version file */
export async function downloadVersion(
  negotiationId: number,
  versionNumber: number,
): Promise<Blob> {
  const response = await apiFetch(
    `${API_V1}/negotiations/${negotiationId}/versions/${versionNumber}/download/`,
  )

  if (!response.ok) {
    throw new Error(`Failed to download version: ${response.status}`)
  }
  return response.blob()
}

/** Download a redline DOCX with Track Changes between two versions. */
export async function downloadRedline(
  negotiationId: number,
  fromVersion: number,
  toVersion: number,
): Promise<Blob> {
  const response = await apiFetch(
    `${API_V1}/negotiations/${negotiationId}/versions/redline/?from=${fromVersion}&to=${toVersion}`,
  )
  if (!response.ok) {
    throw new Error(`Failed to download redline: ${response.status}`)
  }
  return response.blob()
}

export interface VersionListResponse {
  versions: NegotiationVersion[]
  total_count: number
  has_more: boolean
}

/** List versions for a negotiation */
export async function listVersions(
  negotiationId: number,
): Promise<VersionListResponse> {
  const response = await apiFetch(`${API_V1}/negotiations/${negotiationId}/versions/`)
  if (!response.ok) throw new Error(`Failed to fetch versions: ${response.status}`)
  return response.json()
}

/** Paragraph map entry from DOCX extraction */
export interface ParagraphMapEntry {
  para_id: string
  index: number
  text_preview: string
  has_native_id: boolean
}

/** Get paragraph ID map for a specific version */
export async function getVersionParagraphs(
  negotiationId: number,
  versionNumber: number,
): Promise<{ paragraphs: ParagraphMapEntry[]; cached: boolean }> {
  const response = await apiFetch(
    `${API_V1}/negotiations/${negotiationId}/versions/${versionNumber}/paragraphs/`,
  )
  if (!response.ok) throw new Error(`Failed to fetch paragraphs: ${response.status}`)
  return response.json()
}

/** Mark the current actor's party as ready to sign */
export async function markReadyToSign(
  negotiationId: number,
): Promise<{ is_ready_to_sign: boolean }> {
  const response = await apiFetch(`${API_V1}/negotiations/${negotiationId}/ready/`, {
    method: 'POST',
  })
  if (!response.ok) {
    const errorData = await response.json().catch(() => null)
    throw new Error(errorData?.error || `Failed to mark ready: ${response.status}`)
  }
  return response.json()
}

/** Unmark the current actor's party as ready to sign */
export async function unmarkReadyToSign(
  negotiationId: number,
): Promise<{ is_ready_to_sign: boolean }> {
  const response = await apiFetch(`${API_V1}/negotiations/${negotiationId}/unready/`, {
    method: 'DELETE',
  })
  if (!response.ok) {
    const errorData = await response.json().catch(() => null)
    throw new Error(errorData?.error || `Failed to unmark ready: ${response.status}`)
  }
  return response.json()
}

/** Remove a party from the negotiation (owner only, soft-delete) */
export async function removeParty(
  negotiationId: number,
  partyId: number,
): Promise<void> {
  const response = await apiFetch(`${API_V1}/negotiations/${negotiationId}/parties/${partyId}/remove/`, {
    method: 'DELETE',
  })
  if (!response.ok) {
    const errorData = await response.json().catch(() => null)
    throw new Error(errorData?.error || `Failed to remove party: ${response.status}`)
  }
}

/** Send negotiation to signing */
export async function sendToSigning(
  negotiationId: number,
): Promise<{ status: string }> {
  const response = await apiFetch(`${API_V1}/negotiations/${negotiationId}/send-to-signing/`, {
    method: 'POST',
  })
  if (!response.ok) {
    const errorData = await response.json().catch(() => null)
    throw new Error(errorData?.error || `Failed to send to signing: ${response.status}`)
  }
  return response.json()
}

/** Send a visibility-filtered PDF to a party via email. */
export async function sendPdfToParty(
  negotiationId: number,
  partyId: number,
  message?: string,
): Promise<{ success: boolean; email?: string }> {
  const response = await apiFetch(`${API_V1}/negotiations/${negotiationId}/send-pdf/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ party_id: partyId, message }),
  })
  if (!response.ok) {
    const errorData = await response.json().catch(() => null)
    throw new Error(errorData?.error || `Failed to send PDF: ${response.status}`)
  }
  return response.json()
}

// ── Version Diff types ────────────────────────────────────

export interface WordChange {
  type: 'insert' | 'delete' | 'equal'
  text: string
}

export interface DiffBlock {
  type: 'equal' | 'insert' | 'delete' | 'replace'
  paragraph_number_old: number | null
  paragraph_number_new: number | null
  old_text: string
  new_text: string
  word_changes: WordChange[]
}

export interface DiffStats {
  paragraphs_added: number
  paragraphs_removed: number
  paragraphs_modified: number
  paragraphs_unchanged: number
}

export interface VersionDiff {
  from_version: number
  to_version: number
  blocks: DiffBlock[]
  stats: DiffStats
}

/** Get diff between two versions of a negotiation document. */
export async function getVersionDiff(
  negotiationId: number,
  fromVersion: number,
  toVersion: number,
): Promise<VersionDiff> {
  const response = await apiFetch(
    `${API_V1}/negotiations/${negotiationId}/versions/diff/?from=${fromVersion}&to=${toVersion}`,
  )
  if (!response.ok) {
    const errorData = await response.json().catch(() => null)
    throw new Error(errorData?.error || `Failed to get diff: ${response.status}`)
  }
  return response.json()
}

/** Update digest email preference for a party. */
export async function updateDigestPreference(
  negotiationId: number,
  partyId: number,
  digestFrequency: 'NONE' | 'DAILY' | 'WEEKLY',
): Promise<NegotiationParty> {
  const response = await apiFetch(
    `${API_V1}/negotiations/${negotiationId}/parties/${partyId}/digest/`,
    {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ digest_frequency: digestFrequency }),
    },
  )
  if (!response.ok) {
    const errorData = await response.json().catch(() => null)
    throw new Error(errorData?.error || `Failed to update digest: ${response.status}`)
  }
  return response.json()
}


// ── AI Review types & API (Sprint 4.2) ────────────────────────

export interface SuggestedComment {
  paragraph_index: number
  anchor_text: string
  content: string
  category: 'risk' | 'ambiguity' | 'missing_clause' | 'suggestion' | 'compliance'
  severity: 'high' | 'medium' | 'low'
  legal_reference: string | null
}

export interface AIReviewResult {
  success: boolean
  comments: SuggestedComment[]
  summary: string
  risk_score: number
  tokens_used: number
  error: string | null
}

export interface AIReviewStartResponse {
  task_id: string
  status: string
}

export interface AIReviewPollResponse {
  status: 'processing' | 'completed' | 'failed'
  result?: AIReviewResult
  error?: string
}

export interface VisibilitySuggestion {
  visibility: 'INTERNAL' | 'ALL' | 'SPECIFIC'
  target_tag: string | null
  confidence: number
  reason: string
}

/** Start AI contract review (async — returns task_id for polling). */
export async function startAIReview(
  negotiationId: number,
  language: string = 'en',
): Promise<AIReviewStartResponse> {
  const response = await apiFetch(
    `${API_V1}/negotiations/${negotiationId}/ai-review/`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ language }),
    },
  )
  if (!response.ok) {
    const errorData = await response.json().catch(() => null)
    throw new Error(errorData?.error || `Failed to start AI review: ${response.status}`)
  }
  return response.json()
}

/** Poll for AI review result. */
export async function pollAIReview(
  negotiationId: number,
  taskId: string,
): Promise<AIReviewPollResponse> {
  const response = await apiFetch(
    `${API_V1}/negotiations/${negotiationId}/ai-review/${taskId}/`,
  )
  if (!response.ok && response.status !== 202) {
    const errorData = await response.json().catch(() => null)
    throw new Error(errorData?.error || `Failed to poll AI review: ${response.status}`)
  }
  return response.json()
}

/** Apply selected AI suggestions as real comments. */
export async function applyAISuggestions(
  negotiationId: number,
  suggestionIndices: number[],
): Promise<{ success: boolean; created_count: number; comment_ids: number[] }> {
  const response = await apiFetch(
    `${API_V1}/negotiations/${negotiationId}/ai-review/apply/`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ suggestion_indices: suggestionIndices }),
    },
  )
  if (!response.ok) {
    const errorData = await response.json().catch(() => null)
    throw new Error(errorData?.error || `Failed to apply suggestions: ${response.status}`)
  }
  return response.json()
}

// --- Stats types ---

export interface PartyActivity {
  party_label: string
  tag_name: string
  color: string
  comment_count: number
  resolved_count: number
  last_activity: string | null
}

export interface NegotiationStats {
  total_versions: number
  days_since_created: number
  days_since_last_version: number
  avg_days_between_versions: number
  total_comments: number
  resolved_comments: number
  unresolved_comments: number
  resolution_rate: number
  party_activity: PartyActivity[]
  most_active_party: string | null
  avg_response_time_hours: number | null
  fastest_responder: string | null
  ready_count: number
  total_parties: number
}

/** Get statistics for a negotiation. */
export async function getNegotiationStats(
  negotiationId: number,
): Promise<NegotiationStats> {
  const response = await apiFetch(
    `${API_V1}/negotiations/${negotiationId}/stats/`,
  )
  if (!response.ok) {
    throw new Error(`Failed to fetch stats: ${response.status}`)
  }
  return response.json()
}

/** Perform an action on a negotiation (pause, reopen, complete, cancel). */
export async function negotiationAction(
  negotiationId: number,
  action: 'pause' | 'reopen' | 'complete' | 'cancel',
): Promise<{ status: string }> {
  const response = await apiFetch(
    `${API_V1}/negotiations/${negotiationId}/update/`,
    {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action }),
    },
  )
  if (!response.ok) {
    const errorData = await response.json().catch(() => null)
    throw new Error(errorData?.error || `Failed to ${action} negotiation: ${response.status}`)
  }
  return response.json()
}

/** Suggest visibility for a comment. */
export async function suggestVisibility(
  negotiationId: number,
  content: string,
  context: string = '',
): Promise<VisibilitySuggestion> {
  const response = await apiFetch(
    `${API_V1}/negotiations/${negotiationId}/comments/suggest-visibility/`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ content, context }),
    },
  )
  if (!response.ok) {
    const errorData = await response.json().catch(() => null)
    throw new Error(errorData?.error || `Failed to get visibility suggestion: ${response.status}`)
  }
  return response.json()
}

// ── Action Items types & API ────────────────────────────────

export interface ActionItem {
  id: number
  title: string
  description: string
  assigned_party: ActorSummary | null
  status: 'PENDING' | 'IN_PROGRESS' | 'DONE'
  priority: 'LOW' | 'NORMAL' | 'HIGH' | 'URGENT'
  is_blocker: boolean
  due_date: string | null
  comment_id: number | null
  created_by: ActorSummary | null
  created_at: string
  completed_at: string | null
}

export interface CreateActionItemData {
  title: string
  description?: string
  assigned_party_id?: number
  priority?: 'LOW' | 'NORMAL' | 'HIGH' | 'URGENT'
  is_blocker?: boolean
  due_date?: string
  comment_id?: number
}

/** List action items for a negotiation. */
export async function listActionItems(negotiationId: number): Promise<ActionItem[]> {
  const response = await apiFetch(`${API_V1}/negotiations/${negotiationId}/action-items/`)
  if (!response.ok) throw new Error(`Failed to fetch action items: ${response.status}`)
  return response.json()
}

/** Create an action item. */
export async function createActionItem(
  negotiationId: number,
  data: CreateActionItemData,
): Promise<ActionItem> {
  const response = await apiFetch(`${API_V1}/negotiations/${negotiationId}/action-items/create/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  })
  if (!response.ok) {
    const errorData = await response.json().catch(() => null)
    throw new Error(errorData?.error || `Failed to create action item: ${response.status}`)
  }
  return response.json()
}

/** Complete an action item. */
export async function completeActionItem(
  negotiationId: number,
  itemId: number,
): Promise<ActionItem> {
  const response = await apiFetch(
    `${API_V1}/negotiations/${negotiationId}/action-items/${itemId}/complete/`,
    { method: 'POST' },
  )
  if (!response.ok) throw new Error(`Failed to complete action item: ${response.status}`)
  return response.json()
}

/** Reopen a completed action item. */
export async function reopenActionItem(
  negotiationId: number,
  itemId: number,
): Promise<ActionItem> {
  const response = await apiFetch(
    `${API_V1}/negotiations/${negotiationId}/action-items/${itemId}/reopen/`,
    { method: 'POST' },
  )
  if (!response.ok) throw new Error(`Failed to reopen action item: ${response.status}`)
  return response.json()
}

/** Update an action item (partial update). */
export interface UpdateActionItemData {
  title?: string
  description?: string
  assigned_party_id?: number | null
  priority?: 'LOW' | 'NORMAL' | 'HIGH' | 'URGENT'
  is_blocker?: boolean
  due_date?: string | null
}

export async function updateActionItem(
  negotiationId: number,
  itemId: number,
  data: UpdateActionItemData,
): Promise<ActionItem> {
  const response = await apiFetch(
    `${API_V1}/negotiations/${negotiationId}/action-items/${itemId}/`,
    {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    },
  )
  if (!response.ok) {
    const errorData = await response.json().catch(() => null)
    throw new Error(errorData?.error || `Failed to update action item: ${response.status}`)
  }
  return response.json()
}

/** Delete an action item. */
export async function deleteActionItem(
  negotiationId: number,
  itemId: number,
): Promise<void> {
  const response = await apiFetch(
    `${API_V1}/negotiations/${negotiationId}/action-items/${itemId}/delete/`,
    { method: 'DELETE' },
  )
  if (!response.ok) throw new Error(`Failed to delete action item: ${response.status}`)
}

// ── Issues types & API ────────────────────────────────

export type IssueStatus = 'OPEN' | 'PROPOSED' | 'COUNTER_PROPOSED' | 'ACCEPTED' | 'REJECTED' | 'DEFERRED'

export interface NegotiationIssue {
  id: number
  title: string
  description: string
  status: IssueStatus
  category: string
  is_deal_breaker: boolean
  our_position: string
  their_position: string
  agreed_resolution: string
  created_by: ActorSummary | null
  created_at: string
  updated_at: string
}

export interface CreateIssueData {
  title: string
  description?: string
  category?: string
  is_deal_breaker?: boolean
  our_position?: string
}

export interface UpdateIssueData {
  title?: string
  description?: string
  status?: IssueStatus
  category?: string
  is_deal_breaker?: boolean
  our_position?: string
  their_position?: string
  agreed_resolution?: string
}

/** List issues for a negotiation. */
export async function listIssues(negotiationId: number): Promise<NegotiationIssue[]> {
  const response = await apiFetch(`${API_V1}/negotiations/${negotiationId}/issues/`)
  if (!response.ok) throw new Error(`Failed to fetch issues: ${response.status}`)
  return response.json()
}

/** Create an issue. */
export async function createIssue(
  negotiationId: number,
  data: CreateIssueData,
): Promise<NegotiationIssue> {
  const response = await apiFetch(`${API_V1}/negotiations/${negotiationId}/issues/create/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  })
  if (!response.ok) {
    const errorData = await response.json().catch(() => null)
    throw new Error(errorData?.error || `Failed to create issue: ${response.status}`)
  }
  return response.json()
}

/** Update an issue (status, positions, resolution). */
export async function updateIssue(
  negotiationId: number,
  issueId: number,
  data: UpdateIssueData,
): Promise<NegotiationIssue> {
  const response = await apiFetch(`${API_V1}/negotiations/${negotiationId}/issues/${issueId}/`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  })
  if (!response.ok) {
    const errorData = await response.json().catch(() => null)
    throw new Error(errorData?.error || `Failed to update issue: ${response.status}`)
  }
  return response.json()
}

/** Delete an issue. */
export async function deleteIssue(
  negotiationId: number,
  issueId: number,
): Promise<void> {
  const response = await apiFetch(
    `${API_V1}/negotiations/${negotiationId}/issues/${issueId}/delete/`,
    { method: 'DELETE' },
  )
  if (!response.ok) throw new Error(`Failed to delete issue: ${response.status}`)
}

// ── Pre-flight & Finalize types & API ────────────────────────

export interface PreFlightCheck {
  key: string
  label: string
  passed: boolean
  details: string
}

export interface SignerMapping {
  party_id: number
  party_label: string
  tag_name: string
  email: string
  role: string
}

export interface PreFlightResult {
  ready: boolean
  checks: PreFlightCheck[]
  suggested_signers: SignerMapping[]
}

export interface FinalizeRequest {
  signers: SignerMapping[]
  include_comments_pdf?: boolean
}

export interface FinalizeResult {
  success: boolean
  signature_request_id?: number
  signature_request_koblink_id?: string
  errors: string[]
}

/** Run pre-flight checks before signing. */
export async function getPreFlight(negotiationId: number): Promise<PreFlightResult> {
  const response = await apiFetch(`${API_V1}/negotiations/${negotiationId}/pre-flight/`)
  if (!response.ok) {
    const errorData = await response.json().catch(() => null)
    throw new Error(errorData?.error || `Pre-flight check failed: ${response.status}`)
  }
  return response.json()
}

/** Finalize negotiation → create signature request. */
export async function finalizeNegotiation(
  negotiationId: number,
  data: FinalizeRequest,
): Promise<FinalizeResult> {
  const response = await apiFetch(`${API_V1}/negotiations/${negotiationId}/finalize/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  })
  if (!response.ok) {
    const errorData = await response.json().catch(() => null)
    throw new Error(errorData?.error || `Failed to finalize negotiation: ${response.status}`)
  }
  return response.json()
}

// ── Activity Log API ────────────────────────────────

export interface NegotiationActivityEntry {
  id: number
  category: 'version' | 'party' | 'comment' | 'status' | 'signing' | 'system'
  action: string
  description: string
  performed_by_display: ActorSummary | null
  performed_by_name: string
  performed_by_system: boolean
  details: Record<string, unknown>
  created_at: string
}

/** Fetch activity log for a negotiation. */
export async function listNegotiationActivity(
  negotiationId: number,
  limit = 50,
): Promise<NegotiationActivityEntry[]> {
  const response = await apiFetch(
    `${API_V1}/negotiations/${negotiationId}/activity/?limit=${limit}`,
  )
  if (!response.ok) throw new Error(`Failed to fetch activity: ${response.status}`)
  return response.json()
}

// ── PDF Export API ────────────────────────────────

/** Export negotiation as PDF (backend-generated). */
export async function exportNegotiationPdf(
  negotiationId: number,
): Promise<Blob> {
  const response = await apiFetch(
    `${API_V1}/negotiations/${negotiationId}/export/?export_format=pdf`,
  )
  if (!response.ok) throw new Error(`Export failed: ${response.status}`)
  return response.blob()
}

// ── Presence API ──────────────────────────────────

export interface ActiveUser {
  actor_id: string
  display_name: string
  party_label: string
  color: string
  role: string
  last_active_at: string | null
}

/** Send a presence heartbeat for a negotiation. */
export async function postPresence(
  negotiationId: number,
  action = 'viewing',
): Promise<void> {
  await apiFetch(`${API_V1}/negotiations/${negotiationId}/presence/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ action }),
  })
}

/** Get currently active users in a negotiation. */
export async function getActiveUsers(
  negotiationId: number,
): Promise<{ active_users: ActiveUser[] }> {
  const response = await apiFetch(
    `${API_V1}/negotiations/${negotiationId}/active-users/`,
  )
  if (!response.ok) throw new Error(`Failed to fetch active users: ${response.status}`)
  return response.json()
}

// ── AI Import Analysis API ────────────────────────

export interface ImportedParty {
  name: string
  role_hint: string
}

export interface ImportedClause {
  category: string
  title: string
  library_matches: string[]
}

export interface ImportAnalysis {
  task_id: string
  success: boolean
  title: string
  language: string
  parties: ImportedParty[]
  clauses: ImportedClause[]
  key_dates: Array<{ description: string; date: string }>
  financial_terms: Array<{
    description: string
    amount: number | null
    currency: string
  }>
  error: string | null
}

export interface ImportSelections {
  title?: string
  language?: string
  parties?: Array<{ name: string; email: string; role_hint: string }>
  metadata?: {
    key_dates?: Array<{ description: string; date: string }>
    financial_terms?: Array<{
      description: string
      amount: number | null
      currency: string
    }>
  }
}

/** Analyze an imported document with AI. */
export async function analyzeImport(
  negotiationId: number,
): Promise<ImportAnalysis> {
  const response = await apiFetch(
    `${API_V1}/negotiations/${negotiationId}/analyze-import/`,
    { method: 'POST' },
  )
  if (!response.ok) throw new Error(`Analysis failed: ${response.status}`)
  return response.json()
}

/** Apply selected import suggestions to the negotiation. */
export async function applyImportSuggestions(
  negotiationId: number,
  selections: ImportSelections,
): Promise<{ success: boolean }> {
  const response = await apiFetch(
    `${API_V1}/negotiations/${negotiationId}/apply-import-suggestions/`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(selections),
    },
  )
  if (!response.ok) throw new Error(`Apply failed: ${response.status}`)
  return response.json()
}

// ── AI Context Fill API ───────────────────────────

export interface VariableSuggestion {
  variable_name: string
  value: string
  source: 'profile' | 'party_preference' | 'history' | 'ai'
  confidence: number
}

/** AI-powered template variable suggestions. */
export async function suggestTemplateVariables(
  slug: string,
  counterpartyEmail?: string,
): Promise<{ suggestions: VariableSuggestion[] }> {
  const response = await apiFetch(
    `${API_V1}/negotiations/templates/${slug}/suggest-variables/`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ counterparty_email: counterpartyEmail || '' }),
    },
  )
  if (!response.ok) throw new Error(`Suggest failed: ${response.status}`)
  return response.json()
}

// ── Deadline ──────────────────────────────────────────────

/** Update the deadline for a negotiation. Pass null to clear. */
export async function updateDeadline(
  negotiationId: number,
  deadline: string | null,
): Promise<void> {
  const response = await apiFetch(`${API_V1}/negotiations/${negotiationId}/update/`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ deadline }),
  })
  if (!response.ok) {
    const errorData = await response.json().catch(() => null)
    throw new Error(errorData?.error || `Failed to update deadline: ${response.status}`)
  }
}

// ── Playbook Deviations (stub — backend endpoints not yet wired) ──────

export interface PlaybookDeviation {
  id: number
  rule_title: string
  rule_severity: 'blocker' | 'warning' | 'info'
  description: string
  status: 'open' | 'resolved' | 'approved' | 'dismissed'
  created_at: string
}

export async function listDeviations(negotiationId: number): Promise<PlaybookDeviation[]> {
  const response = await apiFetch(`${API_V1}/negotiations/${negotiationId}/playbook/deviations/`)
  if (!response.ok) return []
  return response.json()
}

export async function resolveDeviation(negotiationId: number, deviationId: number, note?: string): Promise<void> {
  await apiFetch(`${API_V1}/negotiations/${negotiationId}/playbook/deviations/${deviationId}/resolve/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ note: note || '' }),
  })
}

export async function approveDeviation(negotiationId: number, deviationId: number, note?: string): Promise<void> {
  await apiFetch(`${API_V1}/negotiations/${negotiationId}/playbook/deviations/${deviationId}/approve/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ note: note || '' }),
  })
}

export async function dismissDeviation(negotiationId: number, deviationId: number, note?: string): Promise<void> {
  await apiFetch(`${API_V1}/negotiations/${negotiationId}/playbook/deviations/${deviationId}/dismiss/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ note: note || '' }),
  })
}
